/**
 * TenantPickerModal
 * Pick-or-create modal for choosing a tenant, mirroring "Lägg till extern
 * kontakt" (AddBuildingExternalContactModal): step one searches existing
 * tenants, step two creates a new one with the ordinary tenant form, and the
 * new tenant comes back selected so it can be confirmed right away.
 */
import React, { useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { Check, Plus } from 'lucide-react';
import { Tenant, TenantInput } from '../../types/tenants';
import { tenantsApi } from '../../api/tenantsApi';
import { useTenants } from '../../hooks/tenants/useTenants';
import { useTenantTerm } from '../../hooks/useTenantTerm';
import TenantSelector from './TenantSelector';
import { TenantForm } from './TenantModal';

interface TenantPickerModalProps {
    show: boolean;
    onHide: () => void;
    onPick: (tenant: Tenant) => void;
    title?: string;
    /** Informational badges per tenant key (see TenantSelector). */
    badges?: { [tenantKey: string]: string };
}

type Step = 'select' | 'create';

const TenantPickerModal: React.FC<TenantPickerModalProps> = ({ show, onHide, onPick, title, badges }) => {
    const term = useTenantTerm().singular.toLowerCase();
    const { tenants, refresh } = useTenants();
    const [step, setStep] = useState<Step>('select');
    const [selected, setSelected] = useState<Tenant | null>(null);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const close = () => {
        setStep('select');
        setSelected(null);
        setError(null);
        onHide();
    };

    const confirm = () => {
        if (!selected) {
            setStep('create');
            return;
        }
        onPick(selected);
        close();
    };

    const create = async (input: TenantInput) => {
        setCreating(true);
        setError(null);
        try {
            const tenant = await tenantsApi.create(input);
            await refresh();
            setSelected(tenant);
            setStep('select');
        } finally {
            setCreating(false);
        }
    };

    return (
        <Modal show={show} onHide={close} centered>
            <Modal.Header closeButton>
                <Modal.Title>{step === 'select' ? title ?? `Välj ${term}` : `Ny ${term}`}</Modal.Title>
            </Modal.Header>
            {step === 'create' ? (
                <TenantForm onSubmit={create} onCancel={() => setStep('select')} submitting={creating} />
            ) : (
                <>
                    <Modal.Body>
                        {error && <Alert variant="danger">{error}</Alert>}
                        <Form.Group>
                            <Form.Label>Välj befintlig {term}</Form.Label>
                            <TenantSelector
                                tenants={tenants}
                                selectedTenant={selected}
                                onSelect={setSelected}
                                onCreateNew={() => setStep('create')}
                                badges={badges}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={close}>Avbryt</Button>
                        <Button variant="primary" onClick={confirm}>
                            {selected
                                ? <><Check size={18} className="me-2" />Välj</>
                                : <><Plus size={18} className="me-2" />Skapa ny {term}</>}
                        </Button>
                    </Modal.Footer>
                </>
            )}
        </Modal>
    );
};

export default TenantPickerModal;
